import { attributes, AttributesMixin, Collection, define, Events, Model } from "@type-r/models";
import { asCurrency, CollapsableSection, Flex, Loading, PrimaryButton, TilesList } from "lqx-react";
import * as React from "react";
import { restfulIO } from 'api/endpoint';
import { useLinked } from "@type-r/react";
import { makeStyles } from "@material-ui/core";
import { useMemo } from "react";
import { ListField, SmallBox } from "../../../inmatch/matching/unreconciled/invoicesList";
import { InvoiceSendingMethod } from "../../../customerProfile/tabs/cp-invoicing";
import { globalStore } from "api";
import { colors } from "lqx-react/src";
import { MarkAsSentDialog } from "../dialogs/mark-as-sent";
import { MarkAsConfirmedDialog } from "../dialogs/mark-as-confirmed";


export interface InvoiceNotSentGroup extends AttributesMixin<typeof InvoiceNotSentGroup> { }
@define
export class InvoiceNotSentGroup extends Model {

    static endpoint = restfulIO('/api/v2/invoices/pending-actions/not-sent');

    static attributes = {
        relationshipId: Number,
        customerName: String,
        customerExternalId: String,
        failedReason: String,
        count: Number,
        outstandingAmountSum: Number,
        sendingMethod: String,
        hasSentDate: Boolean,
        isConfirmed: Boolean
    };

    get isViaInblock(): boolean {
        return !this.hasSentDate
            && inblockSendingMethods.indexOf(this.sendingMethod) >= 0
            && this.failedReason != InvoiceInblockFailedReason.Pending;
    }

    get isOusideInblock(): boolean {
        return !this.hasSentDate
            && inblockSendingMethods.indexOf(this.sendingMethod) < 0;
    }

    get isSentAndWaitingForConfirmation(): boolean {
        return this.hasSentDate
            && inblockSendingMethods.indexOf(this.sendingMethod) < 0
            && !this.isConfirmed;
    }
}

@define 
export class NotSentState extends attributes({
    invoices: [InvoiceNotSentGroup],
    isReady: Boolean
}){
    async fetch(){   
        await this.invoices.fetch();

        this.invoices.updateEach(element => {
            let customer = globalStore.getRelationshipById(element.relationshipId).Customer;
            element.customerName = customer.companyName;
            element.customerExternalId = customer.externalRelationshipId;
        });

        return this;
    } 

    getCount(){
        return this.invoices.reduce((x: number, y) => x + y.count, 0);
    }   
}

const enum InvoiceInblockFailedReason {
    EmailFailed = 'Email Failed',
    MissingPdf = 'Missing PDF',
    EdiFailed = 'Failed EDI',
    MissingEmailFrequency = 'Missing Email Frequency',
    Pending = 'Pending',
    Unknown = 'Unknown'
};

const inblockSendingMethods = [InvoiceSendingMethod.Email as String, InvoiceSendingMethod.InblockEdi as String];

export const NotSentInvoices = ({ state }: { state: NotSentState }) => {

    return (
        state.isReady 
            ? <>
                <OpenItems
                    title={"Open Items via InBlock"}
                    invoices={state.invoices}
                    filterBy={x => x.isViaInblock}
                    type={"not-sent"}></OpenItems>

                <OpenItems
                    title={"Open Items outside of InBlock"}
                    invoices={state.invoices}
                    filterBy={x => x.isOusideInblock}
                    type={"not-sent"}></OpenItems>

                <OpenItems
                    title={"Waiting for confirmation"}
                    invoices={state.invoices}
                    filterBy={x => x.isSentAndWaitingForConfirmation}
                    type={"not-sent"}></OpenItems>
            </>
            : <Loading />
    );
}

export const OpenItems = ({ title, invoices, filterBy, type, renderer,countOverride }:{
    title: String,
    invoices: Collection<any>,
    filterBy: ((m: any) => boolean),
    type?: string,
    renderer? : any,
    countOverride?: number }) => {

    const filtered = useMemo((): InvoiceNotSentGroup[] => {

        return invoices.filter(filterBy);

    }, [(invoices as any)._changeToken]);

    const titleCount = countOverride? countOverride : filtered.reduce((countSum, x) => countSum + x.count, 0);

    return (
        <CollapsableSection title={`${title} (${titleCount})`}>
            {
                filtered.length
                    ? <TilesList
                        items={filtered}
                        itemSize={60}
                        renderer={renderer ? renderer : OpenItem(type)}
                        selectedItem={null}
                        height={150}
                    />
                    : <>No Invoices</>
            }
        </CollapsableSection>
    );
}

const OpenItem = (type) => ({ data, style, index }) => {
    const item = data.items[index] as InvoiceNotSentGroup;
    const $dialog = useLinked(null)
    const styles = openItemStyles();

    return (
        <div style={style}>
            <SmallBox>
                <div className={styles.customerDetails} title={item.customerName}>
                    <Flex column>
                        <ListField className={styles.listField}>{item.customerName}</ListField>
                        <ListField className={styles.listField}>{item.customerExternalId}</ListField>
                    </Flex>
                </div>
                {type=='not-sent' ? <ListField className={styles.listField}>{item.sendingMethod}</ListField>: null}
                <ListField className={styles.listField}>{item.count} Invoices</ListField>
                <ListField className={styles.outstandingAmount}>Sum: {asCurrency(item.outstandingAmountSum)}</ListField>
                {type=='not-sent' ? <ListField className={styles.listField}>{item.failedReason}</ListField> : null}
                <Flex grow></Flex>
                {item.isViaInblock ? <InblockActions invoice={item}></InblockActions> : null}
                {item.isOusideInblock ? <OusideInblockActions $dialog={$dialog} $invoice={item} /> : null}
                {item.isSentAndWaitingForConfirmation ? <IsSentAndWaitingForConfirmationActions $dialog={$dialog} /> : null}
                {type=='overdue' ?
                    <PrimaryButton onClick={() => navigateToInvoicesWithBucketFilter({
                        relationshipId: item.relationshipId,
                        bucket: 'outsideInblock'
                    })}>View Invoices</PrimaryButton>
                : null}
            </SmallBox>
            {
                $dialog.value == "mark-as-sent"
                    ? <MarkAsSentDialog $isOpen={$dialog.equals("mark-as-sent")} relationshipId={item.relationshipId} />
                    : null
            }
            {
                $dialog.value == "mark-as-confirmed"
                    ? <MarkAsConfirmedDialog $isOpen={$dialog.equals("mark-as-confirmed")} relationshipId={item.relationshipId} />
                    : null
            }
        </div>
    )
}

const openItemStyles = makeStyles({
    customerDetails: {
        minWidth: 220,
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "black"
    },
    outstandingAmount: {
        minWidth: 150,
        maxWidth: 150,
        color: "black"
    },
    blueButton: {
        backgroundColor: colors.blue,
        '&:hover': {
            color: 'white',
            boxShadow: '2px 0 4px 0 rgba(0,0,0,0.1)',
            backgroundColor: colors.blue,
        }
    },
    listField: {
        color: "black"
    }
});

const navigateToCustomerProfile = (invoice: InvoiceNotSentGroup) =>
    Events.trigger('navigate', `/customerProfile?relationshipId=${invoice.relationshipId}`);
const navigateToInvoices = () =>
    Events.trigger('navigate', `/invoices/all`);
const navigateToInvoicesWithBucketFilter = (bucketFilter?: any) =>
    Events.trigger('navigate', `/invoices/all`, null, bucketFilter);

const InblockActions = ({ invoice }: { invoice: InvoiceNotSentGroup }) => {
    return (
        invoice.failedReason == InvoiceInblockFailedReason.EmailFailed || invoice.failedReason == InvoiceInblockFailedReason.MissingEmailFrequency
            ? <PrimaryButton onClick={() => navigateToCustomerProfile(invoice)}>View Customer Profile </PrimaryButton>
            : invoice.failedReason == InvoiceInblockFailedReason.MissingPdf
                ? <PrimaryButton onClick={() => navigateToInvoicesWithBucketFilter({
                    relationshipId: invoice.relationshipId,
                    bucket: 'insideInblock',
                    failedReason: InvoiceInblockFailedReason.MissingPdf
                })}> View & Send Invoices</PrimaryButton>
                : invoice.failedReason == InvoiceInblockFailedReason.EdiFailed
                    ? <PrimaryButton onClick={() => navigateToInvoicesWithBucketFilter({
                        relationshipId: invoice.relationshipId,
                        bucket: 'insideInblock',
                        failedReason: InvoiceInblockFailedReason.EdiFailed
                    })}> View Invoices</PrimaryButton>
                    : <></>
    )
}

export const OusideInblockActions = ({$dialog, $invoice}) => {

    const styles = openItemStyles();
    return (
        <>
            <PrimaryButton className={styles.blueButton}
                onClick={() => $dialog.set("mark-as-sent")}
            >
                Mark as Sent
            </PrimaryButton>
            <PrimaryButton onClick={() => navigateToInvoicesWithBucketFilter({
                    relationshipId: $invoice.relationshipId,
                    bucket: 'outsideInblock'
                })}>View Invoices</PrimaryButton>
        </>
    )
}

const IsSentAndWaitingForConfirmationActions = ({$dialog}) => {
    return (
        <PrimaryButton onClick={() => $dialog.set("mark-as-confirmed")}>Mark as Confirmed</PrimaryButton>
    )
}
